import React, { useState } from "react";
import { Button, Col, Collapse, DatePicker, Flex, Modal, Row, Select, Space, Switch, TimeRangePickerProps, Typography } from "antd";
import dayjs from "dayjs";
import ConfigureAds from "./ConfigureAds";
import { LabelText, ModeSegmented, TargetPlatformSelect } from "./StyledComponents";
import { SegmentedValue } from "antd/es/segmented";
import { CopyOutlined, DeleteOutlined, DownloadOutlined, ReloadOutlined, SafetyCertificateOutlined, SaveOutlined } from "@ant-design/icons";
import type { AdConfig } from "./types";
import type { Dayjs } from 'dayjs';
const { RangePicker } = DatePicker;

type CalendarOptionsProps = {
	currentBlockScheduleTitle: string;
	setIsEditModeOn: (isEditModeOn: boolean) => void;
	setShowBlockSchedulePickerModal: (show: boolean) => void;
	setStartDate: (date: Date) => void;
	setEndDate: (date: Date) => void;
	handleRepopulateEpisodesClick: () => void;
	handleValidateEpisodesClick: () => void;
	isValidatingEpisodes: boolean;
	handleSaveCalendarConfigByBlockId: (overrides?: {
		selectedDistributionPlatforms?: string[];
		selectedAmagiCloudPortFeed?: string;
		startDate?: Date;
		endDate?: Date;
		selectedTargetPlatform?: string;
		adConfig?: AdConfig | null;
	}) => void;
	isConfigSaving: boolean;
	isConfigDirty: boolean;
	markCalendarConfigDirty: () => void;
	handleCopyOverEventsFromLastWeek: () => void;
	handleClearWeek: () => void;
	handleClearSchedule: () => void;
	handleDisplayModeChange: (mode: string) => void;
	displayMode: string;
	startDate: Date;
	endDate: Date;
	handleSubmit: () => void;
	selectedTargetPlatform: string;
	targetPlatforms: string[];
	handleTargetPlatformChange: (value: string) => void;
	distributionPlatforms: { id: string; name: string }[];
	selectedDistributionPlatforms: string[];
	handleDistributionPlatformsChange: (values: string[]) => void;
	selectedAmagiCloudPortFeed: string;
	setSelectedAmagiCloudPortFeed: (feedId: string) => void;
	amagiCloudPortFeeds: { feed_id: string; feed_name: string }[];
	handleAmagiCloudPortFeedChange: (feedId: string) => void;
	isDebugMode: boolean;
	setIsDebugMode: (isDebugMode: boolean) => void;
	mySegmentEvents: any[];
	openConfigureAdsModal: boolean;
	setOpenConfigureAdsModal: React.Dispatch<React.SetStateAction<boolean>>;
	adConfig: AdConfig | null;
	setAdConfig: (config: AdConfig | null) => void;
	onNewScheduleClick: () => void;
	onRenameSchedule: (title: string) => void;
};

const { Option } = Select;

const CalendarOptions = ({
    currentBlockScheduleTitle,
    setIsEditModeOn,
    setShowBlockSchedulePickerModal,
    setStartDate,
    setEndDate,
    handleRepopulateEpisodesClick,
    handleValidateEpisodesClick,
    isValidatingEpisodes,
    handleSaveCalendarConfigByBlockId,
    isConfigSaving,
    isConfigDirty,
    markCalendarConfigDirty,
    handleCopyOverEventsFromLastWeek,
    handleClearWeek,
    handleClearSchedule,
    handleDisplayModeChange,
    displayMode,
    startDate,
    endDate,
    handleSubmit,
    selectedTargetPlatform,
    targetPlatforms,
    handleTargetPlatformChange,
    distributionPlatforms,
    selectedDistributionPlatforms,
    handleDistributionPlatformsChange,
    selectedAmagiCloudPortFeed,
    setSelectedAmagiCloudPortFeed,
    amagiCloudPortFeeds,
    handleAmagiCloudPortFeedChange,
    isDebugMode,
    setIsDebugMode,
    mySegmentEvents,
    openConfigureAdsModal,
    setOpenConfigureAdsModal,
    adConfig,
    setAdConfig,
    onNewScheduleClick,
    onRenameSchedule,
}: CalendarOptionsProps) => {

    const [showClearWeekModal, setShowClearWeekModal] = useState(false);
    const [showClearScheduleModal, setShowClearScheduleModal] = useState(false);

    const onRangeChange = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
        if (dates) {
          const nextStartDate = dates[0] ? dates[0].toDate() : startDate;
          const nextEndDate = dates[1] ? dates[1].toDate() : endDate;
          if (dates[0]) setStartDate(nextStartDate);
          if (dates[1]) setEndDate(nextEndDate);
          markCalendarConfigDirty();
        } 
      };
      
      const rangePresets: TimeRangePickerProps['presets'] = [
        { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
        { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
        { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
        { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
      ];

    return (
        <Collapse
            defaultActiveKey={["1"]}
            items={[
                {
                    key: "1",
                    label: "Calendar Options",
                    children: (
                        <>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <Row justify={"space-between"} align={"middle"}>
                                    <Col span={8}>
                                        <Space direction="horizontal" style={{ width: "100%" }}>
                                            <div>
                                                <Typography.Text
                                                    strong
                                                    editable={{
                                                        triggerType: ["text", "icon"],
                                                        tooltip: "Click to rename",
                                                        onChange: onRenameSchedule,
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    {currentBlockScheduleTitle || "Untitled"}
                                                </Typography.Text>
                                                <Button
                                                    type="link"
                                                    onClick={() => {
                                                        // Logic to open the modal dialog
                                                        // setShowTimeModal(true);
                                                        setIsEditModeOn(false);
                                                        setShowBlockSchedulePickerModal(true);
                                                        // console.log('Open modal dialog');
                                                    }}
                                                >
                                                    Open
                                                </Button>
                                                |
                                                <Button
                                                    type="link"
                                                    onClick={onNewScheduleClick}
                                                >
                                                    New
                                                </Button>
                                            </div>
                                        </Space>
                                    </Col>
                                    <Col span={8} style={{ display: "grid", placeItems: "center" }}>
                                        <Flex gap="small" align="center">
                                            <LabelText inline>Mode:</LabelText>
                                            <ModeSegmented
                                                options={["Segments", "Episodes", "Series"]}
                                                value={displayMode}
                                                onChange={(mode: SegmentedValue) => handleDisplayModeChange(mode as string)}
                                            />
                                        </Flex>
                                    </Col>
                                    <Col span={8} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                                        {isConfigSaving && (
                                            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
                                                Saving config...
                                            </span>
                                        )}
                                        <Space direction="horizontal">
                                            <Button
                                                onClick={handleCopyOverEventsFromLastWeek}
                                                style={{ color: "#585858 !important" }}
                                            >
                                                <CopyOutlined />
                                                Copy Prev Week
                                            </Button>
                                            <Button
                                                type="primary"
                                                onClick={() => {
                                                    handleRepopulateEpisodesClick();
                                                }}
                                            >
                                                <ReloadOutlined />
                                                Repopulate Episodes
                                            </Button>
                                        </Space>
                                        <Space direction="horizontal">
                                            <Button
                                                onClick={() => setShowClearWeekModal(true)}
                                                aria-label="Clear current week"
                                            >
                                                <DeleteOutlined />
                                                Clear Week
                                            </Button>
                                            <Button
                                                danger
                                                onClick={() => setShowClearScheduleModal(true)}
                                                aria-label="Clear full schedule"
                                            >
                                                <DeleteOutlined />
                                                Clear Schedule
                                            </Button>
                                        </Space>
                                    </Col>

                                    {/* Clear Week Confirmation Modal */}
                                    <Modal
                                        title="Clear Current Week?"
                                        open={showClearWeekModal}
                                        onCancel={() => setShowClearWeekModal(false)}
                                        footer={[
                                            <Button key="cancel" onClick={() => setShowClearWeekModal(false)}>
                                                Cancel
                                            </Button>,
                                            <Button
                                                key="confirm"
                                                type="primary"
                                                danger
                                                autoFocus={false}
                                                onClick={() => {
                                                    setShowClearWeekModal(false);
                                                    handleClearWeek();
                                                }}
                                            >
                                                Yes, Clear Week
                                            </Button>,
                                        ]}
                                    >
                                        <Typography.Text>
                                            Are you sure you want to clear the current week? All series blocks, episodes, and segments for the week you&apos;re viewing will be removed.
                                        </Typography.Text>
                                    </Modal>

                                    {/* Clear Schedule Confirmation Modal */}
                                    <Modal
                                        title="Clear Full Schedule?"
                                        open={showClearScheduleModal}
                                        onCancel={() => setShowClearScheduleModal(false)}
                                        footer={[
                                            <Button key="cancel" onClick={() => setShowClearScheduleModal(false)}>
                                                Cancel
                                            </Button>,
                                            <Button
                                                key="confirm"
                                                type="primary"
                                                danger
                                                autoFocus={false}
                                                onClick={() => {
                                                    setShowClearScheduleModal(false);
                                                    handleClearSchedule();
                                                }}
                                            >
                                                Yes, Clear Everything
                                            </Button>,
                                        ]}
                                    >
                                        <Typography.Text>
                                            Are you sure you want to clear everything on this schedule? All series blocks, episodes, and segments for all weeks for this schedule will be cleared, leaving you with an empty schedule.
                                        </Typography.Text>
                                    </Modal>
                                </Row>
                                <Row justify={"space-between"} align={"middle"} style={{ margin: "24px 0" }}>
                                    <Col span={8}>
                                        <Flex vertical>
                                            <LabelText>Distribution Partners</LabelText>
                                            <Select
                                                mode="multiple"
                                                style={{ width: "80%" }}
                                                placeholder="Select Distribution Partners"
                                                optionFilterProp="label"
                                                filterSort={(optionA, optionB) =>
                                                    (optionA?.label ?? "")
                                                        .toLowerCase()
                                                        .localeCompare((optionB?.label ?? "").toLowerCase())
                                                }
                                                options={distributionPlatforms.map(partner => ({
                                                    value: partner.id,
                                                    label: partner.name,
                                                }))}
                                                onChange={values => {
                                                    handleDistributionPlatformsChange(values);
                                                    markCalendarConfigDirty();
                                                }}
                                                value={selectedDistributionPlatforms}
                                            />
                                        </Flex>
                                    </Col>
                                    <Col span={8} style={{ display: "grid", placeItems: "center" }}>
                                        <Flex vertical>
                                            <LabelText>CloudPort Segments Feed</LabelText>
                                            <Select
                                                style={{ width: "400px" }}
                                                    placeholder="Select Feed"
                                                    value={selectedAmagiCloudPortFeed || undefined}
                                                options={amagiCloudPortFeeds.map(feed => ({
                                                    value: feed.feed_id,
                                                    label: `${feed.feed_name} (${feed.feed_id})`,
                                                }))}
                                                onChange={values => {
                                                    // Saves itself on change; no Save Config needed.
                                                    handleAmagiCloudPortFeedChange(values);
                                                }}
                                            />
                                        </Flex>
                                    </Col>
                                    <Col span={8} style={{ display: "grid", placeItems: "end" }}>
                                        <Space direction="horizontal" size={"middle"}>
                                            <div>
                                                <LabelText>Date Range</LabelText>
                                                <RangePicker value={[dayjs(startDate), dayjs(endDate)]} presets={rangePresets} onChange={onRangeChange} format="YYYY-MM-DD" style={{ width: "356px" }} />
                                            </div>
                                        </Space>
                                    </Col>
                                </Row>
                                <Row justify={"space-between"}>
                                    <Col span={4}>
                                        <Flex align="center" gap={6}>
                                            <LabelText inline>Debug Mode:</LabelText>
                                            <Switch
                                                size="small"
                                                checked={isDebugMode}
                                                onChange={() => {
                                                    setIsDebugMode(!isDebugMode);
                                                    markCalendarConfigDirty();
                                                }}
                                            />
                                        </Flex>
                                    </Col>
                                    <Col span={20} style={{ textAlign: "right" }}>
                                        <Space>
                                            <LabelText inline>Target Platform (for CSV):</LabelText>
                                            <TargetPlatformSelect
                                                placeholder="Target Platform"
                                                value={selectedTargetPlatform ?? targetPlatforms[0] ?? ''} // Preselect if value matches
                                                onChange={value => {
                                                    // Saves itself on change; no Save Config needed.
                                                    handleTargetPlatformChange(value);
                                                }}
                                                options={targetPlatforms.map(platform => ({
                                                    value: platform,
                                                    label: platform,
                                                }))}
                                            />
                                            <ConfigureAds
                                                openConfigureAdsModal={openConfigureAdsModal}
                                                setOpenConfigureAdsModal={setOpenConfigureAdsModal}
                                                setAdConfig={(config) => {
                                                    // Modal Save commits ad choices directly to the DB in one click:
                                                    // update parent state AND persist. `config` is passed as an
                                                    // override because the adConfig state update isn't applied
                                                    // synchronously within this tick. `null` (Clear all + Save)
                                                    // persists the removal.
                                                    setAdConfig(config);
                                                    markCalendarConfigDirty();
                                                    handleSaveCalendarConfigByBlockId({ adConfig: config });
                                                }}
                                                adConfig={adConfig}
                                                selectedTargetPlatform={selectedTargetPlatform}
                                                selectedAmagiCloudPortFeed={selectedAmagiCloudPortFeed}
                                                selectedCloudPortFeedName={
                                                    amagiCloudPortFeeds.find(
                                                        feed => feed.feed_id === selectedAmagiCloudPortFeed
                                                    )?.feed_name
                                                }
                                            />
                                            <Button
                                                onClick={() => handleSaveCalendarConfigByBlockId()}
                                                loading={isConfigSaving}
                                                disabled={!isConfigDirty}
                                            >
                                                <SaveOutlined />
                                                Save Config
                                            </Button>
                                            {/* Marathon FAST only: verify every episode on the grid
                                                has a complete Marathon (feed 10) segment set before export. */}
                                            {selectedTargetPlatform === 'Marathon FAST' && (
                                                <Button
                                                    onClick={handleValidateEpisodesClick}
                                                    loading={isValidatingEpisodes}
                                                >
                                                    <SafetyCertificateOutlined />
                                                    Validate Episodes
                                                </Button>
                                            )}
                                            <Button onClick={handleSubmit} type="primary">
                                                <DownloadOutlined />
                                                Download CSV
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Space>
                        </>
                    ),
                },
            ]}
        />
    );
};

export default CalendarOptions;
